<template>
	<label
		class="SelectItem"
		:class="{ disabled: item.state === SelectListItemStateType.DISABLED }"
		:for="item.value"
	>
		<div class="Content">
			<slot name="icon"></slot>

			<span
				class="Text"
				:id="`${item.value}-label`"
			>{{ formatText(item.text, subItemsCount) }}</span>
		</div>

		<div class="Content">
			<span
				v-if="item.count"
				class="Count"
				aria-hidden="true"
			>{{ formatCount(item.count) }}</span>

			<input
				:checked="item.state === SelectListItemStateType.SELECTED"
				:id="String(item.value)"
				:type="type"
				:value="item.value"
				:name="name"
				:disabled="item.state === SelectListItemStateType.DISABLED"
				:aria-label="`${item.value}-label`"
				:aria-checked="item.state === SelectListItemStateType.SELECTED"
				:aria-disabled="item.state === SelectListItemStateType.DISABLED"
				class="Input"
				:class="{ 'PartiallySelected': partiallySelected }"
				@change="handleChange"
			/>
		</div>
	</label>
</template>

<script setup lang="ts">
import { type PropType } from 'vue';
import type ISelectItem from '@domain/interfaces/dtos/ISelectItem';
import type { SelectListItemType } from "@domain/enums/SelectListItemType";
import { SelectListItemStateType } from '@domain/enums/SelectListItemStateType';
import { formatCount } from '@utils/formatCount';

const props = defineProps({
	item: {
		type: Object as PropType<ISelectItem>,
		required: true,
	},
	type: {
		type: String as PropType<SelectListItemType>,
		required: true,
	},
	subItemsCount: {
		type: Number,
		required: false,
		default: 0,
	},
	name: {
		type: String,
		required: false,
		default: undefined,
	},
	partiallySelected: {
		type: Boolean,
		required: false,
		default: false,
	}
});

const emit = defineEmits<{
	'selectionChange': [state: string];
}>();

const handleChange = (event: Event) => {
	const target = event.target as HTMLInputElement;
	emit('selectionChange', target.checked ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED);
};

const formatText = (text: string, count: number): string => {
	if (count) {
		return `${text} (${formatCount(count)})`;
	}
	return text;
};
</script>

<style scoped lang="scss">
.SelectItem {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	cursor: pointer;

	&:hover:not(.disabled) {
		.Content .Input {
			outline: 2px solid $Blue-400;
		}
	}

	.Content {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		position: relative;

		.Icon {
			width: 1rem;
			height: 1rem;
			object-fit: contain;
		}

		.Input {
			appearance: none;
			-webkit-appearance: none;
			-moz-appearance: none;
			width: 1.25rem;
			height: 1.25rem;
			border: 1px solid $Grey-600;
			margin: 0;
			flex-shrink: 0;
			transition: border 0.2s ease, border-color 0.2s ease, border-width 0.2s ease, background-color 0.2s ease, outline 0s ease;
			background-color: white;
			cursor: pointer;

			&[type='radio'] {
				border-radius: 50%;

				&:checked {
					border-color: $Blue-700;
					border-width: 0.25rem;
				}
			}

			&[type='checkbox'] {
				border-radius: 0.25rem;
				position: relative;

				&:checked {
					background-color: $Blue-700;
					border-color: $Blue-700;

					&::after {
						content: '';
						position: absolute;
						left: 0.4rem;
						top: 0.1rem;
						width: 0.3rem;
						height: 0.7rem;
						border: solid white;
						border-width: 0 1px 1px 0;
						transform: rotate(45deg);
					}

					&.PartiallySelected::after {
						left: 0.2rem;
						top: 0.55rem;
						width: 0.75rem;
						height: 0;
						border-width: 0 0 1px 0;
						transform: none;
					}
				}
			}

			&:disabled {
				cursor: not-allowed;
			}
		}

		.Text {
			user-select: none;
			color: $Grey-900;
			@include TextStyle(BodyMediumRegular);
		}

		.Count {
			color: $Grey-900;
			@include TextStyle(BodySmallRegular);
		}
	}

	&.disabled {
		cursor: not-allowed;

		.Text {
			color: $Grey-600;
		}

		.Count {
			color: $Grey-600;
		}

		.Input {
			border-color: $Grey-600;
			background-color: $Grey-100;
		}
	}
}
</style>